
import React, { useEffect } from 'react';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    // Prevent background scrolling when the modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup on unmount or when isOpen changes
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 bg-gradient-to-br from-[#0B1D3A] via-[#0F2847] to-[#0B1D3A] px-8 py-6 flex items-center justify-between border-b border-[#D4AF37]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#C9A961] rounded-lg flex items-center justify-center">
              <i className="ri-file-text-line text-xl text-white" aria-hidden="true"></i>
            </div>
            <h2 className="text-xl font-bold text-white">Kullanım Koşulları</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all duration-300 cursor-pointer"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </header>

        {/* Content */}
        <div className="px-8 py-8">
          <div className="space-y-6 text-gray-700 leading-relaxed text-sm">
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-[#0B1D3A] uppercase tracking-wide">
                PRIME SALES PRO
              </h3>
              <p className="text-base font-semibold text-[#0B1D3A] mt-1">
                WEB SİTESİ KULLANIM KOŞULLARI
              </p>
            </div>

            <p>
              Bu web sitesini (www.primesalespro.com) ziyaret eden ve kullanan tüm kullanıcılar,
              aşağıda belirtilen kullanım koşullarını kabul etmiş sayılır. Lütfen siteyi kullanmadan
              önce bu koşulları dikkatle okuyunuz.
            </p>

            {/* Section 1 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">1. Taraflar</h4>
              <p>
                Bu web sitesi, Prime Sales Pro ("Prime Sales Pro") tarafından işletilmektedir.
                Web sitesini kullanan tüm gerçek ve tüzel kişiler ("Kullanıcı") bu kullanım
                koşullarına tabidir.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 2 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">2. Hizmetin Kapsamı</h4>
              <p className="mb-3">Prime Sales Pro, otellere ve konaklama sektörüne yönelik olarak;</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'Satış danışmanlığı',
                  'Kanal yönetimi danışmanlığı',
                  'Teknoloji çözüm danışmanlığı',
                  'Dijital satış ve optimizasyon hizmetleri',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                Web sitesinde yer alan bilgiler genel bilgilendirme amaçlıdır ve bağlayıcı bir
                teklif niteliği taşımaz.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 3 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">3. Web Sitesinin Kullanımı</h4>
              <p className="mb-3">Kullanıcı, web sitesini yalnızca yasal amaçlarla kullanmayı kabul eder.</p>
              <p className="mb-3">Aşağıdaki faaliyetler yasaktır:</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'Web sitesinin güvenliğini ihlal etmeye yönelik girişimler',
                  'Yanıltıcı veya yanlış bilgi vermek',
                  'Siteye zarar verecek teknik müdahaleler',
                  'Yetkisiz erişim girişimleri',
                  'Zararlı yazılım veya virüs yayma girişimleri',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                Prime Sales Pro, bu tür faaliyetleri tespit etmesi durumunda gerekli yasal
                işlemleri başlatma hakkını saklı tutar.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 4 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">4. Fikri Mülkiyet Hakları</h4>
              <p className="mb-3">Web sitesinde yer alan tüm içerikler;</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {['Logo', 'Metinler', 'Görseller', 'Grafikler', 'Tasarım ve yapı'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                Prime Sales Pro&apos;ya aittir veya lisanslı olarak kullanılmaktadır.
                Bu içerikler Prime Sales Pro&apos;nun yazılı izni olmadan kopyalanamaz,
                çoğaltılamaz veya ticari amaçla kullanılamaz.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 5 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">5. Sorumluluğun Sınırlandırılması</h4>
              <p className="mb-3">
                Prime Sales Pro, web sitesinde yer alan bilgilerin doğruluğu ve güncelliği için
                azami özeni gösterir. Ancak:
              </p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'Bilgilerin eksiksiz olduğu garanti edilmez',
                  'Web sitesinin kesintisiz çalışacağı garanti edilmez',
                  'Kullanıcıların site kullanımından doğabilecek doğrudan veya dolaylı zararlardan Prime Sales Pro sorumlu tutulamaz',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-gray-200" />

            {/* Section 6 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">6. Üçüncü Taraf Bağlantıları</h4>
              <p>
                Web sitesi, üçüncü taraf web sitelerine bağlantılar içerebilir. Prime Sales Pro,
                bu sitelerin içeriklerinden veya güvenliğinden sorumlu değildir.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 7 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">7. Gizlilik ve Kişisel Veriler</h4>
              <p>
                Kullanıcıların kişisel verileri, Prime Sales Pro KVKK Aydınlatma Metni kapsamında
                işlenmektedir.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 8 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">8. Değişiklik Hakkı</h4>
              <p>
                Prime Sales Pro, kullanım koşullarını önceden bildirim yapmaksızın değiştirme
                hakkını saklı tutar. Güncel kullanım koşulları web sitesinde yayınlandığı anda
                yürürlüğe girer.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 9 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">9. Uygulanacak Hukuk ve Yetki</h4>
              <p className="mb-2">Bu kullanım koşulları Türkiye Cumhuriyeti kanunlarına tabidir.</p>
              <p>
                Bu koşullardan doğabilecek uyuşmazlıklarda İstanbul Mahkemeleri ve İcra Daireleri
                yetkilidir.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 10 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">10. İletişim</h4>
              <p className="mb-3">Kullanım koşulları ile ilgili sorularınız için:</p>
              <p>
                <span className="font-semibold text-[#0B1DA3]">E-posta: </span>
                <a
                  href="mailto:info@primesalespro.com"
                  className="text-[#D4AF37] hover:underline cursor-pointer"
                >
                  info@primesalespro.com
                </a>
              </p>
              <p className="mt-2">ile iletişime geçebilirsiniz.</p>
            </section>
          </div>

          {/* Footer Button */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C9A961] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
